// 📱 안드로이드 뒤로가기 ↔ 모달/패널 연동 (2026-07-07).
// 문제: 모달이 React state 라 히스토리 항목이 없음 → 하드웨어 뒤로가기가 WebView 히스토리를 빠져나가 앱이 종료됨.
// 해법: 모달이 열릴 때 pushState 로 히스토리 1칸 쌓고, popstate(뒤로가기) 시 최상단 모달의 close 를 호출.
//   X/ESC 로 닫힌 경우엔 쌓아둔 히스토리 1칸을 조용히 되감아(suppress) 뒤로가기 1회가 헛도는 것을 방지.
// 데스크톱/브라우저에서도 "뒤로가기 = 모달 닫기"는 표준 UX 라 전 플랫폼 공통 적용.

type Entry = { id: number; close: () => void };
const stack: Entry[] = [];
let seq = 1;
let inited = false;
let suppressPop = 0;

// 🔴 suppressPop 영구 잔류 방지 (2026-08-07) — 이게 남으면 "뒤로가기를 눌러도 팝업이 안 닫힘" 이 된다.
//   원인: history.back() 은 비동기고, 짧은 간격의 연속 호출을 브라우저가 하나로 합칠 수 있다
//   (모달이 중첩됐다가 한꺼번에 닫힐 때 발생). 기대한 popstate 가 덜 오면 카운터가 남아 이후의
//   진짜 뒤로가기까지 계속 먹어버린다. → 되감기를 요청할 때마다 복구 타이머를 걸어 강제로 0 으로 되돌린다.
const SUPPRESS_RECOVER_MS = 700;
let recoverTimer: number | undefined;
function armRecover() {
	if (typeof window === 'undefined') return;
	if (recoverTimer) window.clearTimeout(recoverTimer);
	recoverTimer = window.setTimeout(() => { suppressPop = 0; recoverTimer = undefined; }, SUPPRESS_RECOVER_MS);
}
function clearRecover() {
	if (recoverTimer !== undefined && typeof window !== 'undefined') window.clearTimeout(recoverTimer);
	recoverTimer = undefined;
}

function init() {
	if (inited || typeof window === 'undefined') return;
	inited = true;
	window.addEventListener('popstate', () => {
		if (suppressPop > 0) {
			suppressPop--;
			if (suppressPop === 0) clearRecover(); else armRecover();
			return;
		}
		const top = stack.pop();
		if (top) {
			try { top.close(); } catch { /* close 실패해도 스택 정리는 유지 */ }
		}
	});
}

// 🔴 되감기 지연 flush (2026-08-20) — "모달 A 닫고 모달 B 열기"(링크 창 → 관리 창)는 같은 tick 에
//   cleanup(back) 과 setup(push) 이 연달아 실행된다. back() 은 비동기라 push 가 먼저 반영되고 나서
//   되감기가 일어나 히스토리 칸과 열린 모달이 어긋났다. → 되감기를 마이크로태스크로 미뤄, 그 사이에
//   새 모달이 열리면 서로 상쇄하고 **같은 칸을 재사용**(replaceState)해 칸 수를 항상 모달 수와 맞춘다.
// 되감을 칸의 id 목록 — 개수만 세던 것을 id 로 바꿔, 실행 시점에 "그 칸이 아직 내 칸인지" 다시 확인한다.
let pendingRewinds: number[] = [];
let flushScheduled = false;
const isMine = (id: number): boolean => {
	try { return (history.state as { tptBack?: number } | null)?.tptBack === id; } catch { return true; /* 접근 불가 — 종전 동작 유지 */ }
};
function rewindOnce(id: number) {
	// 🔴 flush 시점 재확인 (2026-08-27 임시비번 완료 알림 실사고): cleanup(내 칸 확인) 과 flush(back) 사이의 마이크로태스크에서
	//   앱이 라우트를 replaceState 로 바꾸면(알림 '확인' → finishLogin → navigate('/chat', {replace})) 내 칸은 이미 /chat 라우트 칸이다.
	//   여기서 back() 하면 방금 이동한 화면이 사라져 /login 으로 튕겼다(LoginPage 는 그대로 남아 "폼이 다시 뜸"으로 보임).
	if (!isMine(id)) return;
	suppressPop++;
	armRecover();
	try { history.back(); } catch { suppressPop--; if (suppressPop === 0) clearRecover(); }
}
function scheduleFlush() {
	if (flushScheduled) return;
	flushScheduled = true;
	const run = () => {
		flushScheduled = false;
		while (pendingRewinds.length > 0) rewindOnce(pendingRewinds.shift() as number);
	};
	// 마이크로태스크 — React 커밋(cleanup→setup)이 모두 끝난 직후 실행된다.
	if (typeof queueMicrotask === 'function') queueMicrotask(run); else void Promise.resolve().then(run);
}

/** 모달 mount 시 호출 — 반환된 함수를 unmount(cleanup)에서 호출할 것. */
export function pushBackHandler(close: () => void): () => void {
	init();
	const id = seq++;
	stack.push({ id, close });
	let pushed = false;
	try {
		if (pendingRewinds.length > 0) {
			// 직전 모달의 되감기와 상쇄 — 그 칸을 내 것으로 다시 표시해 재사용한다.
			pendingRewinds.pop();
			history.replaceState({ tptBack: id }, '');
		} else {
			history.pushState({ tptBack: id }, '');
		}
		pushed = true;
	} catch { /* 히스토리 불가 환경 — 스택만 동작 */ }
	return () => {
		const idx = stack.findIndex((e) => e.id === id);
		if (idx < 0) return; // 뒤로가기로 이미 소비됨 (popstate 가 pop + close 처리)
		stack.splice(idx, 1);
		if (!pushed) return; // 칸을 못 쌓았으면 되감을 것도 없다 (되감으면 라우트 엔트리를 먹는다)
		// 🔴 지금 히스토리의 최상단이 '내가 쌓은 그 칸' 일 때만 되감는다 (2026-08-20 사용자 신고:
		//   "링크 관리 창을 닫으면 앱 다운로드 페이지로 간다"). 내 칸이 아닌 상태에서 back() 을 부르면
		//   앱이 이전에 방문한 실제 페이지(/download 등)로 튄다.
		if (!isMine(id)) return;
		// X/ESC 닫힘 — 우리가 쌓은 히스토리 1칸 되감기 (다음 뒤로가기가 헛도는 것 방지). 실제 back() 은 flush 때 한 번 더 확인.
		pendingRewinds.push(id);
		scheduleFlush();
	};
}
